import React, {
    useState, useEffect
} from 'react';

import {
    Cctv, Calendar, Image as ImageIcon, Video, Filter, Download, PlusCircle, PlayCircle, Loader2, AlertTriangle, RotateCw, BookOpen, User, X, Info, Columns, Split
} from 'lucide-react';

import axios from 'axios';
import JSZip from 'jszip';
import { formatImageUrl } from '../utils/urlHelper';
const PhotosViewer = () => {
    const [activeTab, setActiveTab] = useState('photos');
    // 'photos' | 'timelapses'
    const [photos, setPhotos] = useState([]);
    const [timelapses, setTimelapses] = useState([]);
    const [bitacoraPhotos, setBitacoraPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [cameraGroups, setCameraGroups] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [timelapseStartDate, setTimelapseStartDate] = useState('');
    const [timelapseEndDate, setTimelapseEndDate] = useState('');
    const [estimatedDuration, setEstimatedDuration] = useState(0);
    const [estimatedCount, setEstimatedCount] = useState(0);
    const [estimating, setEstimating] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedPhotoIds, setSelectedPhotoIds] = useState(new Set());

    // Compare Tool States
    const [comparePhotoA, setComparePhotoA] = useState(null);
    const [comparePhotoB, setComparePhotoB] = useState(null);
    const [sliderPosition, setSliderPosition] = useState(50);
    const [zipping, setZipping] = useState(false);
    
    const activeProjectName = localStorage.getItem('activeProjectName') || 'Proyecto Sin Asignar';

    const userRole = localStorage.getItem('userRole') || 'VIEWER';
    const isAdmin = userRole === 'COMPANY_ADMIN' || userRole === 'SUPER_ADMIN';
    const isDemo = userRole === 'DEMO_USER';

    useEffect(() => {
        fetchData();
        
        // Auto-refresh every 15 seconds silently
        const interval = setInterval(() => {
            fetchData(true);
        }, 15000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    useEffect(() => {
        if (activeTab === 'timelapses') {
            estimateDuration();
        }
    }, [timelapseStartDate, timelapseEndDate, activeTab]);

    const estimateDuration = async () => {
        try {
            setEstimating(true);
            const projectId = localStorage.getItem('activeProjectId');
            const res = await axios.post('http://localhost:4000/api/timelapses/estimate', {
                startDate: timelapseStartDate,
                endDate: timelapseEndDate
            }, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'x-project-id': projectId
                }
            });
            setEstimatedCount(res.data.imageCount);
            setEstimatedDuration(res.data.estimatedDurationSeconds);
        } catch (err) {
            console.error("Error estimando duración:", err);
            setEstimatedCount(0);
            setEstimatedDuration(0);
        } finally {
            setEstimating(false);
        }
    };

    const fetchData = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const projectId = localStorage.getItem('activeProjectId');
            const headers = {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'x-project-id': projectId
            };
            // Fetch photos, timelapses, and logs in parallel
            const [photosRes, timelapsesRes, logsRes] = await Promise.all([
                axios.get('http://localhost:4000/api/photos', { headers }),
                axios.get('http://localhost:4000/api/timelapses', { headers }),
                axios.get('http://localhost:4000/api/logs', { headers }).catch(err => {
                    console.error("Error al obtener bitácoras:", err);
                    return { data: [] };
                })
            ]);

            let filteredPhotos = photosRes.data;
            if (startDate) {
                filteredPhotos = filteredPhotos.filter(p => new Date(p.captured_at) >= new Date(startDate));
            }
            if (endDate) {
                filteredPhotos = filteredPhotos.filter(p => new Date(p.captured_at) <= new Date(endDate));
            }
            setPhotos(filteredPhotos);
            if (filteredPhotos.length > 0) {
                setComparePhotoA(filteredPhotos[filteredPhotos.length - 1]);
                setComparePhotoB(filteredPhotos[0]);
            }

            // Agrupar por cámara
            const grouped = filteredPhotos.reduce((acc, photo) => {
                const camId = photo.camera_id || 'Unknown';
                if (!acc[camId]) acc[camId] = [];
                acc[camId].push(photo);
                return acc;
            }, {});
            setCameraGroups(grouped);

            setTimelapses(timelapsesRes.data);

            // Procesar fotos de la bitácora
            const allBitacoraPhotos = [];
            const logsData = logsRes.data || [];
            logsData.forEach(log => {
                if (startDate && log.log_date < startDate) return;
                if (endDate && log.log_date > endDate) return;

                if (log.image_urls && Array.isArray(log.image_urls)) {
                    log.image_urls.forEach((url, index) => {
                        allBitacoraPhotos.push({
                            id: `${log.id}-${index}`,
                            file_path: url,
                            captured_at: log.log_date,
                            author_name: log.author_name || 'Desconocido',
                            description: log.activities_description || 'Sin descripción',
                            log_id: log.id
                        });
                    });
                }
            });
            setBitacoraPhotos(allBitacoraPhotos);
        } catch (error) {
            console.error("Error al obtener datos:", error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const handleGenerateTimelapse = async () => {
        if (!isAdmin && !isDemo) {
            alert("Modo Demo Activo: Solo los administradores reales pueden generar un timelapse.");
            return;
        }
        try {
            setGenerating(true);
            const projectId = localStorage.getItem('activeProjectId');
            await axios.post('http://localhost:4000/api/timelapses/generate', {
                startDate: timelapseStartDate,
                endDate: timelapseEndDate
            }, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'x-project-id': projectId
                }
            });
            // Simulamos que el worker termina despues de 4.5 segundos para recargar la lista
            setTimeout(() => {
                fetchData();
                setGenerating(false);
            }, 4500);
        } catch (err) {
            console.error("Error generando timelapse:", err);
            alert(err.response?.data?.error || "Hubo un error al generar el timelapse.");
            setGenerating(false);
        }
    };
    const handleDownloadZip = async (photosToDownload) => {
        if (photosToDownload.length === 0) return;
        try {
            setZipping(true);
            const zip = new JSZip();
            
            const promises = photosToDownload.map(async (photo, index) => {
                try {
                    const formattedUrl = formatImageUrl(photo.file_path);
                    const response = await axios.get(formattedUrl, { responseType: 'blob' });
                    const ext = formattedUrl.split('.').pop().split('?')[0] || 'jpg';
                    const dateStr = new Date(photo.captured_at).toISOString().replace(/:/g, '-');
                    const name = `foto_${dateStr}_${index + 1}.${ext}`;
                    zip.file(name, response.data);
                } catch (err) {
                    console.error("Error al descargar imagen para ZIP:", photo.file_path, err);
                }
            });
            
            await Promise.all(promises);
            const content = await zip.generateAsync({ type: 'blob' });
            
            const url = window.URL.createObjectURL(content);
            const link = document.createElement('a');
            link.href = url;
            link.download = `archi_fotos_${new Date().toISOString().slice(0, 10)}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error al compilar el archivo ZIP:", err);
            alert("Hubo un error al compilar el archivo ZIP.");
        } finally {
            setZipping(false);
        }
    };

    const togglePhotoSelection = (photo) => {
        setSelectedPhotoIds(prev => {
            const next = new Set(prev);
            if (next.has(photo.id)) {
                next.delete(photo.id);
            } else {
                next.add(photo.id);
            }
            return next;
        });
    };

    const selectAllFiltered = (currentList) => {
        setSelectedPhotoIds(new Set(currentList.map(p => p.id)));
    };

    const deselectAll = () => {
        setSelectedPhotoIds(new Set());
    };
    
    return (<div className="min-h-screen bg-theme-bg text-theme-text-primary p-4 md:p-8 font-sans"> {
        /* Header */
    } <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4"> <div> <h1 className="text-3xl font-extrabold tracking-tight text-theme-text-primary flex items-center gap-3"> <Cctv className="text-blue-500" size={
        32
    } /> Fotos y Timelapses </h1> <p className="text-theme-text-secondary mt-1">Proyecto: {
        activeProjectName
    }</p> </div> <div className="flex items-center gap-3"> <button onClick={
        () => fetchData(false)
    } disabled={
        loading
    } className="p-2 rounded-xl bg-theme-panel border border-theme-border text-theme-text-secondary hover:text-theme-text-primary transition-all disabled:opacity-50 flex items-center justify-center" title="Actualizar galería"> <RotateCw size={18} className={loading ? "animate-spin text-blue-500" : ""} /> </button> 
                <div className="flex gap-2 bg-theme-panel p-1 rounded-xl">
                    <button
                        onClick={() => setActiveTab('photos')}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                            activeTab === 'photos' ? 'bg-blue-600 text-theme-text-primary' : 'text-theme-text-secondary hover:text-theme-text-primary'
                        }`}
                    >
                        <ImageIcon size={16} /> Galería
                    </button>
                    <button
                        onClick={() => setActiveTab('bitacora')}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                            activeTab === 'bitacora' ? 'bg-blue-600 text-theme-text-primary' : 'text-theme-text-secondary hover:text-theme-text-primary'
                        }`}
                    >
                        <BookOpen size={16} /> Bitácora
                    </button>
                    <button
                        onClick={() => setActiveTab('timelapses')}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                            activeTab === 'timelapses' ? 'bg-blue-600 text-theme-text-primary' : 'text-theme-text-secondary hover:text-theme-text-primary'
                        }`}
                    >
                        <Video size={16} /> Timelapses
                    </button>
                    <button
                        onClick={() => setActiveTab('compare')}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                            activeTab === 'compare' ? 'bg-blue-600 text-theme-text-primary' : 'text-theme-text-secondary hover:text-theme-text-primary'
                        }`}
                    >
                        <Columns size={16} /> Comparador
                    </button>
                </div> </div> </div> <div className="max-w-7xl mx-auto"> {
        loading ? (<div className="flex justify-center items-center py-20"> <Loader2 className="animate-spin text-blue-500" size={
            48
        } /> </div>) : activeTab === 'photos' ? (<> {
            /* Filters */
        } <div className="mb-6 bg-theme-panel p-4 rounded-2xl border border-theme-border flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
                <div className="flex items-center gap-2 text-theme-text-secondary"> <Filter size={
                    18
                } /> <span className="text-sm font-semibold">Filtrar:</span> </div> <div className="flex items-center gap-3"> <input type="date" className="bg-theme-bg border border-theme-border rounded-lg px-3 py-2 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500" value={
                    startDate
                } onChange={
                    (e) => setStartDate(e.target.value)
                } /> <span className="text-theme-text-secondary">hasta</span> <input type="date" className="bg-theme-bg border border-theme-border rounded-lg px-3 py-2 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500" value={
                    endDate
                } onChange={
                    (e) => setEndDate(e.target.value)
                } /> </div> {
                    (startDate || endDate) && (<button onClick={
                        () => {
                            setStartDate('');
                            setEndDate('');
                        }
                    } className="text-sm text-blue-400 hover:text-blue-300"> Limpiar </button>)
                }
            </div>
            
            <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto border-t lg:border-t-0 pt-3 lg:pt-0 border-theme-border/50">
                <button 
                    onClick={() => {
                        setIsSelectionMode(!isSelectionMode);
                        setSelectedPhotoIds(new Set());
                    }} 
                    className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all border ${
                        isSelectionMode 
                        ? 'bg-blue-600 border-blue-500 text-white' 
                        : 'bg-theme-bg border-theme-border text-theme-text-secondary hover:text-theme-text-primary'
                    }`}
                >
                    {isSelectionMode ? 'Cancelar Selección' : 'Seleccionar Múltiples'}
                </button>

                {isSelectionMode ? (
                    <>
                        <button 
                            onClick={() => selectAllFiltered(photos)}
                            className="px-3 py-2 bg-theme-bg hover:bg-theme-panel border border-theme-border rounded-xl text-xs font-semibold text-theme-text-primary"
                        >
                            Seleccionar Todas
                        </button>
                        <button 
                            onClick={deselectAll}
                            className="px-3 py-2 bg-theme-bg hover:bg-theme-panel border border-theme-border rounded-xl text-xs font-semibold text-theme-text-primary"
                        >
                            Limpiar
                        </button>
                        <button 
                            onClick={() => handleDownloadZip(photos.filter(p => selectedPhotoIds.has(p.id)))}
                            disabled={selectedPhotoIds.size === 0 || zipping}
                            className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-lg shadow-emerald-600/10"
                        >
                            {zipping ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                            Descargar ZIP ({selectedPhotoIds.size})
                        </button>
                    </>
                ) : (
                    <button 
                        onClick={() => handleDownloadZip(photos)}
                        disabled={photos.length === 0 || zipping}
                        className="px-3.5 py-2 bg-blue-600/10 hover:bg-blue-600/20 border border-blue-500/20 disabled:border-theme-border disabled:bg-zinc-900 disabled:text-zinc-600 text-blue-400 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5"
                    >
                        {zipping ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                        Descargar Todo Filtrado (ZIP)
                    </button>
                )}
            </div>
        </div> {
                /* Photo Grid */
            } {
                photos.length === 0 ? (<div className="text-center bg-theme-panel p-12 rounded-3xl border border-theme-border"> <ImageIcon size={
                    64
                } className="mx-auto text-gray-600 mb-4" /> <h3 className="text-xl font-bold text-theme-text-primary">No hay fotos encontradas</h3> <p className="text-theme-text-secondary mt-2">Intenta ajustar los filtros de fecha o asegúrate de que las cámaras estén transmitiendo.</p> </div>) : (<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"> {
                    photos.map(photo => (<div key={
                        photo.id
                    } className={`bg-theme-panel rounded-2xl overflow-hidden border transition-all cursor-pointer relative ${
                        selectedPhotoIds.has(photo.id) ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-theme-border hover:border-gray-500'
                    }`} onClick={() => isSelectionMode ? togglePhotoSelection(photo) : setSelectedPhoto(photo)}> <div className="relative h-48 overflow-hidden bg-theme-bg"> <img src={
                        formatImageUrl(photo.file_path)
                    } alt="Evidencia" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    
                    {isSelectionMode && (
                        <div className="absolute top-2 left-2 z-10">
                            <input 
                                type="checkbox" 
                                checked={selectedPhotoIds.has(photo.id)} 
                                onChange={() => togglePhotoSelection(photo)}
                                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    )}
                    
                    <div className="absolute top-2 right-2 bg-theme-bg/60 backdrop-blur text-xs px-2 py-1 rounded text-theme-text-primary flex items-center gap-1"> <Cctv size={
                        12
                    } /> IA: {
                                    photo.person_count
                                } </div> </div> <div className="p-4"> <p className="text-theme-text-secondary text-xs font-mono mb-2 flex items-center gap-2"> <Calendar size={
                                    14
                                } /> {
                                        new Date(photo.captured_at).toLocaleString()
                                    } </p> <a href={formatImageUrl(photo.file_path)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-400 hover:text-blue-300 text-sm font-semibold flex items-center gap-1">
                                <Download size={14} /> Ver Original
                            </a>
                        </div>
                    </div>
                    ))}
                </div>)}
        </>
        ) : activeTab === 'bitacora' ? (
        <>
            {/* Filters */}
            <div className="mb-6 bg-theme-panel p-4 rounded-2xl border border-theme-border flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
                    <div className="flex items-center gap-2 text-theme-text-secondary">
                        <Filter size={18} />
                        <span className="text-sm font-semibold">Filtrar:</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <input
                            type="date"
                            className="bg-theme-bg border border-theme-border rounded-lg px-3 py-2 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                        <span className="text-theme-text-secondary">hasta</span>
                        <input
                            type="date"
                            className="bg-theme-bg border border-theme-border rounded-lg px-3 py-2 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    {(startDate || endDate) && (
                        <button
                            onClick={() => {
                                setStartDate('');
                                setEndDate('');
                            }}
                            className="text-sm text-blue-400 hover:text-blue-300"
                        >
                            Limpiar
                        </button>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto border-t lg:border-t-0 pt-3 lg:pt-0 border-theme-border/50">
                    <button 
                        onClick={() => {
                            setIsSelectionMode(!isSelectionMode);
                            setSelectedPhotoIds(new Set());
                        }} 
                        className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all border ${
                            isSelectionMode 
                            ? 'bg-blue-600 border-blue-500 text-white' 
                            : 'bg-theme-bg border-theme-border text-theme-text-secondary hover:text-theme-text-primary'
                        }`}
                    >
                        {isSelectionMode ? 'Cancelar Selección' : 'Seleccionar Múltiples'}
                    </button>

                    {isSelectionMode ? (
                        <>
                            <button 
                                onClick={() => selectAllFiltered(bitacoraPhotos)}
                                className="px-3 py-2 bg-theme-bg hover:bg-theme-panel border border-theme-border rounded-xl text-xs font-semibold text-theme-text-primary"
                            >
                                Seleccionar Todas
                            </button>
                            <button 
                                onClick={deselectAll}
                                className="px-3 py-2 bg-theme-bg hover:bg-theme-panel border border-theme-border rounded-xl text-xs font-semibold text-theme-text-primary"
                            >
                                Limpiar
                            </button>
                            <button 
                                onClick={() => handleDownloadZip(bitacoraPhotos.filter(p => selectedPhotoIds.has(p.id)))}
                                disabled={selectedPhotoIds.size === 0 || zipping}
                                className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-lg shadow-emerald-600/10"
                            >
                                {zipping ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                                Descargar ZIP ({selectedPhotoIds.size})
                            </button>
                        </>
                    ) : (
                        <button 
                            onClick={() => handleDownloadZip(bitacoraPhotos)}
                            disabled={bitacoraPhotos.length === 0 || zipping}
                            className="px-3.5 py-2 bg-blue-600/10 hover:bg-blue-600/20 border border-blue-500/20 disabled:border-theme-border disabled:bg-zinc-900 disabled:text-zinc-600 text-blue-400 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5"
                        >
                            {zipping ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                            Descargar Todo Filtrado (ZIP)
                        </button>
                    )}
                </div>
            </div>

            {/* Bitacora Photo Grid */}
            {bitacoraPhotos.length === 0 ? (
                <div className="text-center bg-theme-panel p-12 rounded-3xl border border-theme-border">
                    <BookOpen size={64} className="mx-auto text-gray-600 mb-4" />
                    <h3 className="text-xl font-bold text-theme-text-primary">No hay fotos de bitácora</h3>
                    <p className="text-theme-text-secondary mt-2">
                        Las fotos que agregues en el registro diario de bitácora aparecerán aquí.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {bitacoraPhotos.map(photo => (
                        <div key={photo.id} className={`bg-theme-panel rounded-2xl overflow-hidden border flex flex-col justify-between transition-all cursor-pointer relative ${
                            selectedPhotoIds.has(photo.id) ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-theme-border hover:border-gray-500'
                        }`} onClick={() => isSelectionMode ? togglePhotoSelection(photo) : setSelectedPhoto(photo)}>
                            <div>
                                <div className="relative h-48 overflow-hidden bg-theme-bg animate-fade-in">
                                    {isSelectionMode && (
                                        <div className="absolute top-2 left-2 z-10">
                                            <input 
                                                type="checkbox" 
                                                checked={selectedPhotoIds.has(photo.id)} 
                                                onChange={() => togglePhotoSelection(photo)}
                                                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    )}
                                    <img
                                        src={formatImageUrl(photo.file_path)}
                                        alt="Evidencia Bitácora"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    <div className="absolute top-2 right-2 bg-theme-bg/60 backdrop-blur text-[10px] px-2 py-1 rounded text-theme-text-primary font-bold uppercase tracking-wider">
                                        Bitácora
                                    </div>
                                </div>
                                <div className="p-4">
                                    <p className="text-theme-text-secondary text-xs font-mono mb-1.5 flex items-center gap-2">
                                        <Calendar size={14} />
                                        {photo.captured_at}
                                    </p>
                                    <p className="text-theme-text-secondary text-xs mb-3 flex items-center gap-1.5 font-medium">
                                        <User size={14} className="text-blue-400" />
                                        <span>Autor: {photo.author_name}</span>
                                    </p>
                                    <p className="text-theme-text-primary text-xs line-clamp-3 bg-theme-bg/40 p-2.5 rounded-xl border border-theme-border/50 italic text-zinc-300">
                                        "{photo.description}"
                                    </p>
                                </div>
                            </div>
                            <div className="p-4 pt-0">
                                <a
                                    href={formatImageUrl(photo.file_path)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-blue-400 hover:text-blue-300 text-sm font-semibold flex items-center gap-1 mt-2"
                                >
                                    <Download size={14} /> Ver Original
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
        ) : activeTab === 'compare' ? (
            <div className="space-y-6">
                <div className="bg-theme-panel border border-theme-border rounded-3xl p-6 shadow-xl relative overflow-hidden">
                    <div className="absolute right-0 top-0 w-32 h-32 bg-blue-500/5 rounded-full blur-2xl"></div>
                    <h3 className="text-xl font-bold mb-1 flex items-center gap-2 text-theme-text-primary">
                        <Split className="text-blue-500 text-blue-400" /> Comparador de Avance (Antes y Después)
                    </h3>
                    <p className="text-xs text-theme-text-secondary mb-6">
                        Selecciona dos fotografías de la galería para deslizar y comparar el progreso físico en el mismo encuadre.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider flex items-center gap-1.5">
                                🟢 Foto Inicial (Antes / Izquierda)
                            </label>
                            <select
                                value={comparePhotoA ? comparePhotoA.id : ''}
                                onChange={(e) => {
                                    const selected = photos.find(p => p.id === parseInt(e.target.value));
                                    if (selected) setComparePhotoA(selected);
                                }}
                                className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50"
                            >
                                <option value="">-- Elige una Foto --</option>
                                {[...photos].reverse().map(p => (
                                    <option key={p.id} value={p.id}>
                                        {new Date(p.captured_at).toLocaleString()} {p.camera_id ? `(Cámara ${p.camera_id})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-theme-text-secondary uppercase tracking-wider flex items-center gap-1.5">
                                🔵 Foto Final (Después / Derecha)
                            </label>
                            <select
                                value={comparePhotoB ? comparePhotoB.id : ''}
                                onChange={(e) => {
                                    const selected = photos.find(p => p.id === parseInt(e.target.value));
                                    if (selected) setComparePhotoB(selected);
                                }}
                                className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                            >
                                <option value="">-- Elige una Foto --</option>
                                {photos.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {new Date(p.captured_at).toLocaleString()} {p.camera_id ? `(Cámara ${p.camera_id})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* COMPARADOR SLIDER INTERACTIVO */}
                    {comparePhotoA && comparePhotoB ? (
                        <div className="relative w-full aspect-video max-h-[70vh] rounded-3xl overflow-hidden border border-theme-border select-none bg-black flex items-center justify-center">
                            {/* Imagen A (Antes) en el fondo */}
                            <img 
                                src={formatImageUrl(comparePhotoA.file_path)} 
                                alt="Progreso Inicial" 
                                className="absolute inset-0 w-full h-full object-contain pointer-events-none" 
                            />
                            
                            {/* Imagen B (Después) recortada con clipPath */}
                            <img 
                                src={formatImageUrl(comparePhotoB.file_path)} 
                                alt="Progreso Final" 
                                className="absolute inset-0 w-full h-full object-contain pointer-events-none" 
                                style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
                            />

                            {/* Línea del Slider Vertical */}
                            <div 
                                className="absolute top-0 bottom-0 w-1 bg-white shadow-xl z-20 pointer-events-none" 
                                style={{ left: `${sliderPosition}%` }}
                            >
                                {/* Botón / Handle en el centro */}
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white text-zinc-900 flex items-center justify-center shadow-2xl border border-zinc-300 font-black">
                                    <Split size={18} className="rotate-90 md:rotate-0" />
                                </div>
                            </div>

                            {/* Control deslizante invisible interactivo */}
                            <input 
                                type="range" 
                                min="0" 
                                max="100" 
                                value={sliderPosition} 
                                onChange={(e) => setSliderPosition(parseInt(e.target.value))} 
                                className="absolute inset-0 opacity-0 cursor-ew-resize z-30 w-full h-full"
                            />
                        </div>
                    ) : (
                        <div className="bg-theme-bg/50 border border-theme-border/50 rounded-2xl p-12 text-center text-zinc-500 italic">
                            Por favor selecciona ambas fotos en los menús de arriba para activar la comparación interactiva.
                        </div>
                    )}
                </div>
            </div>
        ) : ( /* Timelapses Section */
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-900/50 to-gray-800 p-6 rounded-3xl border border-blue-500/20 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex-1">
                        <h3 className="text-xl font-bold text-theme-text-primary mb-1">Generador de Timelapses</h3>
                        <p className="text-theme-text-secondary text-sm mb-4">Compila las imágenes más recientes de la obra en un video de alta velocidad.</p>
                        
                        {/* Selector de Rango de Fechas */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-theme-bg/50 p-4 rounded-xl border border-theme-border/50">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-theme-text-secondary">Desde:</span>
                                <input type="date" className="bg-theme-bg border border-theme-border rounded-lg px-2 py-1 text-sm text-theme-text-primary" 
                                    value={timelapseStartDate} onChange={(e) => setTimelapseStartDate(e.target.value)} />
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-theme-text-secondary">Hasta:</span>
                                <input type="date" className="bg-theme-bg border border-theme-border rounded-lg px-2 py-1 text-sm text-theme-text-primary" 
                                    value={timelapseEndDate} onChange={(e) => setTimelapseEndDate(e.target.value)} />
                            </div>
                            
                            {(timelapseStartDate || timelapseEndDate) && (
                                <button onClick={() => { setTimelapseStartDate(''); setTimelapseEndDate(''); }} className="text-xs text-blue-400 hover:text-blue-300">Limpiar</button>
                            )}
                        </div>

                        {/* Texto de estimación */}
                        <div className="mt-3 text-sm">
                            {estimating ? (
                                <span className="text-yellow-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Calculando...</span>
                            ) : estimatedCount > 0 ? (
                                <span className="text-emerald-400 font-medium">
                                    📸 {estimatedCount} fotos encontradas &nbsp;&rarr;&nbsp; ⏱️ Video estimado: {estimatedDuration} segundos
                                </span>
                            ) : (
                                <span className="text-red-400 font-medium">No se encontraron fotos en el rango seleccionado.</span>
                            )}
                        </div>
                    </div>
                    <button onClick={
                        handleGenerateTimelapse
                    } disabled={
                        generating || (!isAdmin && !isDemo) || estimatedCount === 0 || estimating
                    } className={
                        `px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all ${
                            generating || estimatedCount === 0
                            ? 'bg-gray-700 text-theme-text-secondary cursor-not-allowed' 
                            : (!isAdmin && !isDemo) 
                            ? 'bg-blue-950/20 text-blue-500/40 border border-blue-500/10 cursor-not-allowed select-none'
                            : 'bg-blue-600 hover:bg-blue-500 text-theme-text-primary shadow-lg shadow-blue-500/30 active:scale-95'
                        }`
                    } > {
                            generating ? <><Loader2 size={
                                18
                            } className="animate-spin" /> Compilando fotogramas...</> : <><PlusCircle size={
                                18
                            } /> Compilar Timelapse</>
                        } </button> </div>
                
                {(!isAdmin && !isDemo) && (
                    <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-2xl flex items-start gap-3 text-xs text-yellow-500 font-medium">
                        <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                        <div>
                            <strong>Modo Vista/Demo:</strong> La generación de timelapses requiere de procesamiento de video FFmpeg y almacenamiento. Esta función está deshabilitada para usuarios invitados.
                        </div>
                    </div>
                )}
                {isDemo && (
                    <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex items-start gap-3 text-xs text-blue-400 font-medium">
                        <Info size={18} className="shrink-0 mt-0.5" />
                        <div>
                            <strong>Modo Demostración:</strong> Como usuario invitado en la Demo, puedes generar hasta un máximo de 1 timelapse para probar el motor de compilación.
                        </div>
                    </div>
                )} {
                    timelapses.length === 0 ? (<div className="text-center bg-theme-panel p-12 rounded-3xl border border-theme-border"> <Video size={
                        64
                    } className="mx-auto text-gray-600 mb-4" /> <h3 className="text-xl font-bold text-theme-text-primary">No hay videos generados</h3> <p className="text-theme-text-secondary mt-2">Haz clic en "Compilar Timelapse" para crear tu primer video del avance.</p> </div>) : (<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"> {
                        timelapses.map(video => (<div key={
                            video.id
                        } className="bg-theme-panel rounded-2xl overflow-hidden border border-theme-border p-4"> <div className="aspect-video bg-theme-bg rounded-xl mb-4 overflow-hidden relative group flex items-center justify-center"> <video src={
                            formatImageUrl(video.file_path)
                        } controls className="w-full h-full object-contain" /> </div> <h4 className="font-bold text-theme-text-primary mb-1 flex items-center gap-2"> <PlayCircle size={
                            16
                        } className="text-blue-400" /> Avance de Obra </h4> <p className="text-xs text-theme-text-secondary font-mono"> Generado: {
                            new Date(video.captured_at).toLocaleString()
                        } </p> </div>))
                    } </div>)
                } </div>)
    } </div> 
    
    {/* Image Modal */}
    {selectedPhoto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setSelectedPhoto(null)}>
            <div className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center justify-center animate-in fade-in zoom-in duration-200" onClick={(e) => e.stopPropagation()}>
                <button 
                    onClick={() => setSelectedPhoto(null)}
                    className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white bg-black/40 hover:bg-black/60 rounded-full transition-all"
                >
                    <X size={24} />
                </button>
                <img 
                    src={formatImageUrl(selectedPhoto.file_path)} 
                    alt="Evidencia Ampliada" 
                    className="w-full h-full object-contain rounded-lg shadow-2xl" 
                    style={{ maxHeight: 'calc(90vh - 60px)' }}
                />
                <div className="mt-4 text-center">
                    <p className="text-white/90 font-mono">Capturada: {new Date(selectedPhoto.captured_at).toLocaleString()}</p>
                    <a href={formatImageUrl(selectedPhoto.file_path)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors text-sm font-semibold">
                        <Download size={16} /> Descargar Imagen
                    </a>
                </div>
            </div>
        </div>
    )}
    </div >);
};
export default PhotosViewer;

